import {
	Cache,
	ClampToEdgeWrapping,
	EquirectangularReflectionMapping,
	MeshPhysicalMaterial,
	MeshStandardMaterial,
	SRGBColorSpace,
	Texture,
	TextureLoader,
	UVMapping
} from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

// Enable Three.js built-in file cache so FileLoader deduplicates
// concurrent requests for the same URL and serves subsequent loads from memory.
Cache.enabled = true;

import type { TextureInfo } from '../../../model/texture-info';
import type { TextureManagerConfig, TextureLoadCallbacks } from './types';
import { loadTexturesCfn } from './functions/load-textures.cfn';
import { createMaterialCfn } from './functions/create-material.cfn';
import { disposeCfn } from './functions/dispose.cfn';
import { setEnvironmentTextureCfn } from './functions/set-environment-texture.cfn';
import { setEnvironmentTextureAsyncCfn } from './functions/set-environment-texture-async.cfn';
import { IncrementalMaterialUpdater } from './incremental-material-updater';
import { Ktx2Support } from './helpers/ktx2-support';
import { PmremPreloader } from './pmrem-preloader';

/**
 * TextureManager handles texture loading, caching, and material creation.
 *
 * Features:
 * - Load textures (diffuse, normal, alpha, displacement)
 * - HDR texture support
 * - Material creation with PBR properties
 * - Texture caching
 * - Environment map management
 */
export class TextureManager {
	#config: TextureManagerConfig;

	// Texture caches
	#textures = new Map<string, Texture>();
	#normalMaps = new Map<string, Texture>();
	#alphaMaps = new Map<string, Texture>();
	#displacementMaps = new Map<string, Texture>();
	#materials = new Map<string, MeshStandardMaterial | MeshPhysicalMaterial>();

	// Environment map
	#environmentMap: Texture | null = null;

	// Non-blocking texture upgrade support
	#pmremPreloader: PmremPreloader;
	#materialUpdater: IncrementalMaterialUpdater;

	// Cached loader instances (reused across loadTextures calls)
	#textureLoader: TextureLoader;
	#hdrLoader: RGBELoader;

	// KTX2/Basis compressed texture support
	#ktx2Support: Ktx2Support;

	// Keys whose loads failed permanently (all retries exhausted)
	#failedKeys: Set<string> = new Set();

	constructor(config: TextureManagerConfig) {
		this.#config = config;
		this.#pmremPreloader = new PmremPreloader();
		this.#materialUpdater = new IncrementalMaterialUpdater();
		this.#textureLoader = new TextureLoader();
		this.#hdrLoader = new RGBELoader();
		this.#ktx2Support = new Ktx2Support();

		// Initialize PMREM preloader if renderer is available
		if (config.renderer) {
			this.#pmremPreloader.initialize(config.renderer);
		}

		// Initialize KTX2 support if renderer and transcoder path are available
		if (config.renderer && config.ktx2TranscoderPath) {
			this.#ktx2Support.initialize(config.renderer, config.ktx2TranscoderPath);
		}
	}

	// ─────────────────────────────────────────────────────────────────────────────
	// Public getters for external read access
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Get the environment map
	 */
	get environmentMap(): Texture | null {
		return this.#environmentMap;
	}

	/**
	 * Set the environment map
	 */
	set environmentMap(texture: Texture | null) {
		this.#environmentMap = texture;
	}

	/**
	 * Get the configuration
	 */
	get config(): TextureManagerConfig {
		return this.#config;
	}

	/**
	 * Get the textures map
	 */
	get textures(): Map<string, Texture> {
		return this.#textures;
	}

	/**
	 * Get the normal maps
	 */
	get normalMaps(): Map<string, Texture> {
		return this.#normalMaps;
	}

	/**
	 * Get the alpha maps
	 */
	get alphaMaps(): Map<string, Texture> {
		return this.#alphaMaps;
	}

	/**
	 * Get the displacement maps
	 */
	get displacementMaps(): Map<string, Texture> {
		return this.#displacementMaps;
	}

	/**
	 * Get all cached material keys
	 */
	getMaterialKeys(): string[] {
		return Array.from(this.#materials.keys());
	}

	/**
	 * Get all cached texture keys
	 */
	getTextureKeys(): string[] {
		return Array.from(this.#textures.keys());
	}

	/**
	 * Get materials map for external use
	 */
	get materials(): Map<string, MeshStandardMaterial | MeshPhysicalMaterial> {
		return this.#materials;
	}

	/**
	 * Get the PMREM preloader instance
	 */
	get pmremPreloader(): PmremPreloader {
		return this.#pmremPreloader;
	}

	/**
	 * Get the material updater instance
	 */
	get materialUpdater(): IncrementalMaterialUpdater {
		return this.#materialUpdater;
	}

	/**
	 * Get the KTX2 support instance
	 */
	get ktx2Support(): Ktx2Support {
		return this.#ktx2Support;
	}

	/** Keys that failed to load permanently after all retries. */
	get failedKeysAccess (): Set<string> {
		return this.#failedKeys;
	}

	/** Record a key that permanently failed to load. */
	recordFailedKey (key: string): void {
		this.#failedKeys.add(key);
	}

	/** Clear failure tracking (called on engine generation bump). */
	clearFailedKeys (): void {
		this.#failedKeys.clear();
	}

	/**
	 * Get the cached TextureLoader instance
	 */
	get textureLoader(): TextureLoader {
		return this.#textureLoader;
	}

	/**
	 * Get the cached RGBELoader instance
	 */
	get hdrLoader(): RGBELoader {
		return this.#hdrLoader;
	}

	// ─────────────────────────────────────────────────────────────────────────────
	// Public methods
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Get a cached texture by key
	 */
	getTexture(key: string): Texture | undefined {
		return this.#textures.get(key);
	}

	/**
	 * Get a cached material by key
	 */
	getMaterial(key: string): MeshStandardMaterial | MeshPhysicalMaterial | undefined {
		return this.#materials.get(key);
	}

	/**
	 * Check if a texture is cached
	 */
	hasTexture(key: string): boolean {
		return this.#textures.has(key);
	}

	/**
	 * Check if a material is cached
	 */
	hasMaterial(key: string): boolean {
		return this.#materials.has(key);
	}

	/**
	 * Update configuration
	 */
	updateConfig(config: Partial<TextureManagerConfig>): void {
		this.#config = { ...this.#config, ...config };
	}

	/**
	 * Load textures from TextureInfo array
	 */
	async loadTextures(textureInfos: TextureInfo[], callbacks: TextureLoadCallbacks = {}): Promise<void> {
		return loadTexturesCfn(this, { textureInfos, callbacks });
	}

	/**
	 * Create a material from TextureInfo
	 */
	createMaterial(tx: TextureInfo): MeshStandardMaterial | MeshPhysicalMaterial {
		return createMaterialCfn(this, { textureInfo: tx });
	}

	/**
	 * Load a custom sky texture from a data URL.
	 * Uses UVMapping for flat viewport fill (not EquirectangularReflectionMapping)
	 * so user-uploaded images display without spherical distortion.
	 * Does NOT set this as the environment map — reflections stay on REFLECTION_SKY_KEY.
	 */
	loadCustomSkyTexture(dataUrl: string): Promise<Texture> {
		return new Promise((resolve, reject) => {
			const textureLoader = new TextureLoader();
			textureLoader.load(
				dataUrl,
				(texture: Texture) => {
					texture.mapping = UVMapping;
					texture.colorSpace = SRGBColorSpace;
					texture.wrapS = ClampToEdgeWrapping;
					texture.wrapT = ClampToEdgeWrapping;
					resolve(texture);
				},
				undefined,
				(error) => {
					console.error('Error loading custom sky texture:', error);
					reject(error);
				}
			);
		});
	}

	/**
	 * Dispose of all resources
	 */
	dispose(): void {
		disposeCfn(this, {});
	}

	/**
	 * Create materials for all loaded textures
	 * This is a public method for cfn function access
	 */
	createMaterials(textureInfos: TextureInfo[]): void {
		// Set environment map from any loaded sky texture (not just 'sky/overcast').
		// First check the incoming textureInfos for a sky entry, then fall back to
		// searching the textures cache for any sky/ key.
		if (!this.#environmentMap) {
			let skyKey: string | null = null;
			for (const tx of textureInfos) {
				if (tx.key.startsWith('sky/') && this.#textures.has(tx.key)) {
					skyKey = tx.key;
					break;
				}
			}
			if (!skyKey) {
				for (const key of this.#textures.keys()) {
					if (key.startsWith('sky/')) {
						skyKey = key;
						break;
					}
				}
			}
			if (skyKey) {
				const skyTexture = this.#textures.get(skyKey)!;
				if (this.#pmremPreloader.isInitialized) {
					const envMap = this.#pmremPreloader.generatePmremSync(skyKey, skyTexture);
					this.#environmentMap = envMap ?? skyTexture;
				} else {
					this.#environmentMap = skyTexture;
				}
			}
		}

		for (const tx of textureInfos) {
			if (tx.key.startsWith('sky/')) continue;
			const material = this.createMaterial(tx);
			this.#materials.set(tx.key, material);
		}
	}

	/**
	 * Get a cloned material by key (for applying to scene objects)
	 */
	getClonedMaterial(key: string): MeshStandardMaterial | MeshPhysicalMaterial | null {
		const material = this.#materials.get(key);
		if (material) {
			const cloned = material.clone();
			cloned.needsUpdate = true;
			return cloned;
		}
		return null;
	}

	/**
	 * Update environment texture for reflections
	 */
	setEnvironmentTexture(key: string): boolean {
		return setEnvironmentTextureCfn(this, { key });
	}

	/**
	 * Update environment texture for reflections without blocking the UI.
	 * Uses pre-loaded PMREM and incremental material updates.
	 * Falls back to synchronous version if PMREM is not pre-loaded.
	 */
	async setEnvironmentTextureAsync(
		key: string,
		onProgress?: (completed: number, total: number) => void
	): Promise<boolean> {
		return setEnvironmentTextureAsyncCfn(this, { key, onProgress });
	}

	/**
	 * Pre-load PMREM for a texture key during idle time.
	 * Call this after loading a texture to prepare it for non-blocking use.
	 */
	preloadPmrem(key: string): Promise<Texture> | null {
		const texture = this.#textures.get(key);
		if (!texture) {
			console.warn('[TextureManager] Cannot preload PMREM - texture not found:', key);
			return null;
		}
		return this.#pmremPreloader.preload(key, texture);
	}

	/**
	 * Check if PMREM is pre-loaded for a texture key
	 */
	isPmremReady(key: string): boolean {
		return this.#pmremPreloader.isReady(key);
	}
}
